import { useQuery } from '@apollo/client';
import { FileAttachmentsWidget, FileAttachmentsWidgetEditFeature } from '@procureai/ui-modules-mui';
import { useTranslation } from 'react-i18next';

import {
  getAttachmentFragments,
  getStatusByParentVisibilities,
  groupAttachmentsByVisibilityParents,
  useFileAttachmentsUpdateMutation,
  FILE_ACCEPTS,
  classificationOptions,
} from '../../FileAttachmentsWidget/FileAttachmentsWidget.util';
import { TsrctAttachmentParentVariant, TsrctUploadedByVariant } from '@/generated/gql/graphql';
import { isContractDemandEditable } from '@/legacy/common/guards/contractDemand.guards';
import { I18nNamespace } from '@/legacy/common/types/I18nNamespace';
import { useModuleContext } from '@/legacy/components/ModuleContextProvider';

import {
  CONTRACT_DEMAND_FILE_ATTACHMENTS_DEMAND_QUERY,
  CONTRACT_DEMAND_FILE_ATTACHMENTS_QUERY,
} from './ContractDemandFileAttachmentsWidget.gql';

export const ContractDemandFileAttachmentsWidget = () => {
  const { entityId: contractDemandId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDemandFileAttachmentsWidget' });
  const contractDemandParent = {
    variant: TsrctAttachmentParentVariant.CONTRACT_DEMAND,
    id: contractDemandId!,
  };

  const { data: demandData, loading: contractDemandLoading } = useQuery(CONTRACT_DEMAND_FILE_ATTACHMENTS_DEMAND_QUERY, {
    variables: {
      demandId: contractDemandId!,
    },
  });

  const sourcingCaseIds =
    demandData?.tesseract.contractDemand.sourcingCases.map((sourcingCase) => sourcingCase.id) || [];

  const variables = {
    parents: [contractDemandParent],
    filter: {
      uploadedByVariants: [TsrctUploadedByVariant.INTERNAL_USER],
    },
  };

  const { data, loading: attachmentsLoading } = useQuery(CONTRACT_DEMAND_FILE_ATTACHMENTS_QUERY, {
    variables,
  });

  const { save, saving } = useFileAttachmentsUpdateMutation({
    refetchQueries: [{ query: CONTRACT_DEMAND_FILE_ATTACHMENTS_QUERY, variables }],
    parent: contractDemandParent,
  });

  const loading = contractDemandLoading || attachmentsLoading;

  const attachments = getAttachmentFragments(data?.tesseract.attachments);
  const visibilitiesByParentId = groupAttachmentsByVisibilityParents(attachments);

  return (
    <>
      <FileAttachmentsWidget
        editFeatures={[FileAttachmentsWidgetEditFeature.UPLOAD, FileAttachmentsWidgetEditFeature.CHANGE_VISIBILITY]}
        uploadButtonLabel={t('upload.buttonLabel')}
        attachments={attachments}
        loading={loading}
        onSave={save}
        saving={saving}
        getItemStatus={(attachment) => {
          return getStatusByParentVisibilities(attachment, visibilitiesByParentId, sourcingCaseIds);
        }}
        isItemDeletable={(attachment) => attachment.parent.id === contractDemandId}
        isEditable={demandData ? isContractDemandEditable(demandData.tesseract.contractDemand) : false}
        accept={FILE_ACCEPTS}
        classificationOptions={classificationOptions}
        getItemClassification={(attachment) => attachment.classification}
      />
    </>
  );
};
